import { useState } from 'react'
import { faCity, faHeart } from '@fortawesome/pro-duotone-svg-icons'

import Icon from '../icon/Icon'
import MatchScoreBadge from '../match-score-badge/MatchScoreBadge'
import {
  formatBedsBathsLine,
  formatMoveInLabel,
  formatPriceLabel,
} from '../../pages/discover/helpers'
import {
  Address,
  BadgeRow,
  Body,
  CardRoot,
  Footer,
  ImageElement,
  ImagePlaceholder,
  ImageWrapper,
  ManagedBy,
  Meta,
  MoveInLabel,
  Price,
  SaveButton,
  StatusBadge,
  Title,
  TitleRow,
} from './styled'
import { DiscoverListingCardProps } from './types'

const STATUS_LABELS: Record<
  'available' | 'available-soon' | 'occupied' | 'unavailable',
  string
> = {
  available: 'Available',
  'available-soon': 'Available soon',
  occupied: 'Occupied',
  unavailable: 'Off-market',
}

/**
 * Single listing card for the discover grid. Click anywhere except the
 * save heart navigates to the detail page. Save toggle is optimistic via
 * the `isSaved` prop owned by the parent so multiple cards re-render
 * cheaply.
 *
 * @param {DiscoverListingCardProps} props
 * @returns {JSX.Element}
 */
const DiscoverListingCard = ({
  listing,
  isSaved,
  onSaveToggle,
}: DiscoverListingCardProps): JSX.Element => {
  const [isToggling, setIsToggling] = useState<boolean>(false)
  const detailPath = `/discover/${listing.id}`
  const firstImage = listing.imageUrls[0]
  const statusLabel = STATUS_LABELS[listing.availability] ?? 'Unavailable'

  const handleSaveClick = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    event.preventDefault()
    event.stopPropagation()
    if (isToggling === true) {
      return
    }

    setIsToggling(true)
    try {
      await onSaveToggle(listing.id)
    } finally {
      setIsToggling(false)
    }
  }

  return (
    <CardRoot>
      <ImageWrapper to={detailPath}>
        {firstImage !== undefined ? (
          <ImageElement src={firstImage} alt={listing.title} loading="lazy" />
        ) : (
          <ImagePlaceholder aria-hidden="true">
            <Icon icon={faCity} label="No photo" />
          </ImagePlaceholder>
        )}
        <BadgeRow>
          <StatusBadge tone={listing.availability}>{statusLabel}</StatusBadge>
          <SaveButton
            type="button"
            isSaved={isSaved}
            onClick={handleSaveClick}
            disabled={isToggling}
            aria-label={isSaved === true ? 'Remove from saved' : 'Save listing'}
            title={isSaved === true ? 'Remove from saved' : 'Save listing'}
          >
            <Icon icon={faHeart} />
          </SaveButton>
        </BadgeRow>
      </ImageWrapper>

      <Body>
        <TitleRow>
          <Title to={detailPath}>{listing.title}</Title>
          <MatchScoreBadge score={listing.matchScore} size="sm" />
        </TitleRow>
        <Address>
          {listing.compiledAddress !== ''
            ? listing.compiledAddress
            : listing.city}
        </Address>
        <Price>{formatPriceLabel(listing)}</Price>
        <Meta>{formatBedsBathsLine(listing)}</Meta>
        <Footer>
          <MoveInLabel>{formatMoveInLabel(listing)}</MoveInLabel>
          {listing.propertyManagerName !== '' ? (
            <ManagedBy>by {listing.propertyManagerName}</ManagedBy>
          ) : null}
        </Footer>
      </Body>
    </CardRoot>
  )
}

export default DiscoverListingCard
